import { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { announceToLiveRegion } from '@trello/a11y';
import { useCanceledPairs } from '@trello/business-logic-react/action';
import { sendErrorEvent } from '@trello/error-reporting';
import { optimisticIdManager } from '@trello/graphql';
import { intl } from '@trello/i18n';
import { BoardIdProvider, WorkspaceIdProvider } from '@trello/id-context';
import { Button } from '@trello/nachos/button';
import { showFlag } from '@trello/nachos/experimental-flags';
import { usePreviousWhileLoading } from '@trello/workspaces';

import { Action, ActionsEmpty } from 'app/src/components/Action';
import { WorkspacePageSpinner } from 'app/src/components/WorkspacePages/WorkspacePageSpinner';
import {
  useMemberActivityListLazyQuery,
  useMemberActivityListQuery,
  type MemberActivityListQuery,
} from './MemberActivityListQuery.generated';

interface MemberActivityListProps {
  memberId: string;
}

export const MemberActivityList = ({ memberId }: MemberActivityListProps) => {
  const [page, setPage] = useState(0);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const resultLimit = 10;
  const [loadMoreActions] = useMemberActivityListLazyQuery();
  const firstNewActionRef = useCallback((node: HTMLElement) => {
    node?.focus();
  }, []);
  const lastActionCount = useRef(0);

  const { loading, data } = useMemberActivityListQuery({
    variables: {
      memberId,
      limit: resultLimit,
      page,
    },
    waitOn: ['None'],
    // eslint-disable-next-line @trello/disallow-fetch-policies
    fetchPolicy: 'network-only',
  });

  const allActions: MemberActivityListQuery['memberActions'] =
    usePreviousWhileLoading(data?.memberActions ?? [], loading, []);

  const actions = useCanceledPairs(allActions);

  const retrieveNextPage = useCallback(() => {
    lastActionCount.current = actions.length;
    loadMoreActions({
      variables: {
        memberId,
        limit: resultLimit,
        page: page + 1,
      },
    })
      .then(() => {
        setPage(page + 1);
      })
      .catch((error) => {
        showFlag({
          id: 'memberActivityListLoadMoreError',
          title: 'Error loading activity',
          appearance: 'error',
        });
        sendErrorEvent(error, {
          tags: {
            ownershipArea: 'trello-web-eng',
            feature: 'Member Activity',
          },
        });
      });
    setIsInitialLoad(false);
  }, [loadMoreActions, memberId, page, lastActionCount, actions]);

  useEffect(() => {
    if (!isInitialLoad) {
      if (loading) {
        announceToLiveRegion(
          intl.formatMessage({
            id: 'templates.member_activity.load-more-activity-loading',
            defaultMessage: 'Loading more activities...',
            description:
              'Status indicator that more activities are being loaded for a member, for users of assistive technology',
          }),
        );
      } else {
        announceToLiveRegion(
          intl.formatMessage({
            id: 'templates.member_activity.load-more-activity-complete',
            defaultMessage: 'Activity list updated',
            description:
              'Status indicator that more activities for a member have been loaded and the list has been updated, for users of assistive technology',
          }),
        );
      }
    }
  }, [actions, isInitialLoad, loading]);

  if (loading && actions.length === 0) {
    return <WorkspacePageSpinner />;
  }

  if (actions.length === 0) {
    return <ActionsEmpty daysBack={30} />;
  }

  return (
    <ul>
      {actions.map((action, i) => {
        const dataJson = JSON.parse(action.data);

        const boardIdContext = dataJson.board?.id ?? '';
        const workspaceIdContext = dataJson.organization?.id ?? '';
        const isFirstNewAction =
          lastActionCount.current > 0 && i === lastActionCount.current;

        return (
          <WorkspaceIdProvider value={workspaceIdContext} key={action.id}>
            <BoardIdProvider value={boardIdContext}>
              <Action
                key={optimisticIdManager.getStableIdKey(action.id)}
                id={action.id}
                location="member"
                memberId={memberId}
                date={action.date}
                translationKey={action.display.translationKey}
                entities={action.display.entities}
                reactions={[]}
                boardName={dataJson.board?.name}
                firstFocusableRef={
                  isFirstNewAction ? firstNewActionRef : undefined
                }
              />
            </BoardIdProvider>
          </WorkspaceIdProvider>
        );
      })}
      <Button onClick={retrieveNextPage} isLoading={loading}>
        <FormattedMessage
          id="templates.member_activity.load-more-activity"
          defaultMessage="Load more activity"
          description="Button to load more activity for a member"
        />
      </Button>
    </ul>
  );
};
